import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useAuth } from '../../../contexts/AuthContext';

interface Category {
  id: string;
  name: string;
}

interface ExpenseFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (expense: any) => void;
  expense?: any;
  categories?: Category[]; // Optional: for category dropdown
}

const defaultExpense = {
  date: '',
  category_id: '',
  description: '',
  amount: '',
  receipt_url: '',
};

const ExpenseFormDrawer: React.FC<ExpenseFormDrawerProps> = ({ open, onClose, onSave, expense, categories = [] }) => {
  // eslint-disable-next-line no-console
  console.log('ExpenseFormDrawer render – open =', open);
  const [form, setForm] = useState(defaultExpense);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const { token, currentBusiness, API_BASE_URL } = useAuth();

  useEffect(() => {
    if (expense) {
      setForm({
        date: expense.date || '',
        category_id: expense.category_id || '',
        description: expense.description || '',
        amount: expense.amount || '',
        receipt_url: expense.receipt_url || '',
      });
    } else {
      setForm(defaultExpense);
    }
  }, [expense, open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => firstInputRef.current?.focus());
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  const createCategory = async (name: string): Promise<Category | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name, type: 'expense' })
      });
      if (!res.ok) throw new Error('Failed to create category');
      const newCategory = await res.json();
      
      // Refresh the categories list
      // await refreshCategories(); // This line was not in the new_code, so it's removed.
      
      return newCategory;
    } catch (e) {
      alert('Failed to create category');
      return null;
    }
  };

  return open ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="expense-drawer-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onKeyDown={e => e.key === 'Escape' && onClose()}
    >
      <div className="bg-white dark:bg-gray-800 p-6 w-[32rem] rounded-lg outline-none">
        <h2 id="expense-drawer-title" className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          {expense ? 'Edit Expense' : 'Add Expense'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date
            </label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              required
              ref={firstInputRef}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <Select
              name="category_id"
              value={categories.find((cat: Category) => cat.id === form.category_id) || null}
              onChange={(option: Category | null) => setForm({ ...form, category_id: option ? option.id : '' })}
              options={categories}
              getOptionLabel={(option: Category) => option.name}
              getOptionValue={(option: Category) => option.id}
              isClearable
              placeholder={categories.length === 0 ? 'No categories available' : 'Select or type to search'}
              noOptionsMessage={({ inputValue }: { inputValue: string }) => (
                <div>
                  No categories found.
                  <button
                    type="button"
                    className="ml-2 text-blue-600 underline text-xs"
                    onMouseDown={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setShowAddCategory(true);
                      setNewCategoryName(inputValue);
                    }}
                  >
                    Add new category
                  </button>
                </div>
              )}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              menuPortalTarget={document.body}
            />
            {showAddCategory && (
              <div className="mt-2 flex gap-2 items-center">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCategoryName(e.target.value)}
                  className="border px-2 py-1 rounded text-sm"
                  placeholder="New category name"
                />
                <button
                  type="button"
                  className="px-2 py-1 bg-blue-600 text-white rounded"
                  onClick={async () => {
                    const created = await createCategory(newCategoryName);
                    if (created) {
                      setForm({ ...form, category_id: created.id });
                      setShowAddCategory(false);
                      setNewCategoryName('');
                    }
                  }}
                >Add</button>
                <button type="button" className="px-2 py-1 bg-gray-200 rounded" onClick={() => setShowAddCategory(false)}>Cancel</button>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <input
              type="text"
              name="description"
              value={form.description}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Amount
            </label>
            <input
              type="number"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Receipt URL (optional)
            </label>
            <input
              type="url"
              name="receipt_url"
              value={form.receipt_url}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              ref={addBtnRef}
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {expense ? 'Update Expense' : 'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null;
};

export default ExpenseFormDrawer; 